import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

// Auth interceptor - attach token if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('alphagrade_auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Error response normalizer + structured tier-error dispatch
api.interceptors.response.use(
  (response) => {
    // Sliding sessions (2026-09-22). The API hands back a fresh token once the
    // current one is past halfway through its life; swapping it in here is what
    // keeps an active user signed in instead of being thrown out on a fixed
    // schedule. Wrapped because storage can throw in private windows, and a
    // failed save must cost a future login, never this response.
    try {
      const fresh = response.headers?.['x-refreshed-token'];
      if (fresh) localStorage.setItem('alphagrade_auth_token', fresh);
    } catch {
      // ignore; the existing token stays valid until it expires
    }
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    // Expired or invalid JWT → clear local token and redirect to a clean
    // re-login flow. Without this, a 401 surfaces as a generic
    // "Not authenticated" error mid-app and the user is stuck.
    // Skip if the failing request was the login itself (so a wrong
    // password doesn't trigger a redirect loop) — the auth UI handles
    // its own 401s.
    if (status === 401) {
      const url = error.config?.url || '';
      const isAuthRequest = url.includes('/auth/login') || url.includes('/auth/register');
      // Only bounce to a clean re-login if the user ACTUALLY had a token (a
      // genuine expired session). Anonymous visitors browsing open/public pages
      // have no token — a 401 from an authed endpoint must NOT redirect them to
      // '/', or open-access browsing is impossible. Let the caller handle it.
      const hadToken = !!localStorage.getItem('alphagrade_auth_token');
      if (!isAuthRequest && hadToken) {
        localStorage.removeItem('alphagrade_auth_token');
        // Friendly hint for the login screen (read once and cleared by the
        // auth UI). Keeps the message visible even after the redirect.
        try {
          sessionStorage.setItem(
            'alphagrade_auth_message',
            'Your session expired. Please sign in again.'
          );
        } catch {
          // sessionStorage may be unavailable in some embedded contexts; ignore.
        }
        // Use replace so the back button doesn't return to the broken state.
        if (window.location.pathname !== '/' && window.location.pathname !== '/login') {
          window.location.replace('/');
        }
        return Promise.reject(new Error('Session expired'));
      }
    }

    // Detect structured tier errors (limit_reached / upgrade_required)
    // and dispatch a global event so the LimitReachedModal can show.
    if (
      (status === 429 || status === 403) &&
      detail &&
      typeof detail === 'object' &&
      (detail.error === 'limit_reached' || detail.error === 'upgrade_required')
    ) {
      window.dispatchEvent(new CustomEvent('alphagrade:tier-limit', { detail }));
      const err = new Error(detail.message || 'Limit reached');
      err.tierDetail = detail;
      return Promise.reject(err);
    }

    const message =
      (typeof detail === 'string' ? detail : detail?.message) ||
      error.message ||
      'Request failed';
    return Promise.reject(new Error(message));
  }
);

export default api;
